import { onBeforeUnmount, ref, watch } from 'vue'
import type { Ref } from 'vue'

function getElementWidth(element: HTMLElement | null): number {
  if (!element) {
    return 0
  }
  return element.clientWidth || element.getBoundingClientRect().width || 0
}

/**
 * 观察某个 DOM 元素的宽度变化（对应 React 端 useElementWidth）。
 *
 * 使用：
 * ```ts
 * const { elRef, width } = useElementWidth<HTMLDivElement>()
 * // <div ref="elRef" />
 * ```
 *
 * 内部：
 * - 优先使用 ResizeObserver 精确观察容器尺寸
 * - 降级到 window resize 事件
 * - 组件卸载时自动 disconnect / removeEventListener
 */
export function useElementWidth<T extends HTMLElement = HTMLElement>(): {
  elRef: Ref<T | null>
  width: Ref<number>
} {
  const elRef = ref<T | null>(null) as Ref<T | null>
  const width = ref(0)

  let observer: ResizeObserver | null = null
  let resizeHandler: (() => void) | null = null

  const cleanup = () => {
    if (observer) {
      observer.disconnect()
      observer = null
    }
    if (resizeHandler) {
      window.removeEventListener('resize', resizeHandler)
      resizeHandler = null
    }
  }

  const measure = (target: T) => {
    width.value = getElementWidth(target)
  }

  watch(
    elRef,
    (target) => {
      cleanup()
      if (!target) {
        return
      }
      measure(target)

      if (typeof ResizeObserver !== 'undefined') {
        observer = new ResizeObserver(() => measure(target))
        observer.observe(target)
      } else {
        resizeHandler = () => measure(target)
        window.addEventListener('resize', resizeHandler)
      }
    },
    { flush: 'post', immediate: true },
  )

  onBeforeUnmount(cleanup)

  return { elRef, width }
}